import { isEmpty, extend, noop, chain } from 'underscore';
import { v4 as uuidv4 } from 'uuid';
import { stringifySync } from '@bingads-webui/safe-json';
import { SurveyDataSource } from './survey-data-source';

const defaultTestAppId = 50025;
const defaultAppId = 2482;

export function SurveyDataService({
  isTestEnv = true,
  surveyId,
  surveyName,
  Interface,
  Product,
  customParams,
  dataSource,
  serverContext,
  tokenInfo,
  isReportingPipelineEnabled = false,
  logActivity = noop,
  sendSourcePageURI = true,
}) {
  const appId = isTestEnv ? defaultTestAppId : defaultAppId;

  if (isEmpty(dataSource)) {
    dataSource = new SurveyDataSource(); // eslint-disable-line no-param-reassign
  }

  const { cid, aid, uid } = dataSource.getScope();

  const defaultFeedback = {
    type: 'Survey',
    manifestType: 'Fps',
    survey: {
      surveyId,
    },
    comment: '',
    rating: 1,
    email: '',
    web: {
      sourcePageURI: sendSourcePageURI ? chain(window).result('location').result('href').value() || '' : '',
    },
    application: {
      appData: stringifySync({
        ...customParams,
      }),
    },
    complianceChecks: {
      authenticationType: 'MSA',
      ageGroup: 'Adult',
    },
    sdkVersion: 'AdsWeb 1.0.0',
  };

  const defaultTelemetry = {
    tenantId: tokenInfo ? String(tokenInfo.TenantId) : null, // MSA/AAD tenant Id
    loggableUserId: tokenInfo ? String(tokenInfo.UserId) : null, // MSA/AAD user Id
    featureArea: '',
    deviceType: '',
    processSessionId: chain(serverContext).result('Omniture').result('SessionId').value() || undefined,
  };

  return {
    sendFeedback: ({
      feedback,
      telemetry,
      extendedManifestData,
      onFinishSendRequest = noop,
    }) => {
      const clientFeedbackId = uuidv4();

      let surveySpecificData = {
        cid,
        aid,
        uid,
      };

      if (extendedManifestData != null) {
        surveySpecificData = {
          ...(extendedManifestData.surveySpecificData || {}),
          ...surveySpecificData,
        };
      }

      defaultFeedback.application.extendedManifestData = stringifySync({
        surveySpecificData: stringifySync(surveySpecificData),
      });

      const feedbackWithDefaults = extend(defaultFeedback, feedback);

      const telemetryWithDefaults = extend(defaultTelemetry, telemetry);

      if (!isTestEnv && isReportingPipelineEnabled) {
        const originalCallback = onFinishSendRequest;
        onFinishSendRequest = (...args) => { // eslint-disable-line no-param-reassign
          originalCallback.apply(this, args);
          dataSource.sendEventToReportingPipeline({
            surveyName,
            Interface,
            Product,
            feedback: feedbackWithDefaults,
            telemetry: telemetryWithDefaults,
            clientFeedbackId,
            serverContext,
            logActivity,
          });
        };
      }

      dataSource.sendFeedback({
        appId,
        clientFeedbackId,
        isTestEnv,
        feedback: feedbackWithDefaults,
        telemetry: telemetryWithDefaults,
        onSuccess: () => {
          onFinishSendRequest();
          logActivity(`SurveyDataService | survey: ${surveyName}, action: send OCV feedback success`);
        },
        onError: () => {
          onFinishSendRequest();
          logActivity(`SurveyDataService | survey: ${surveyName}, action: send OCV feedback failed`);
        },
      });
    },
  };
}
